<template>
  <BackofficeLayout>
    <div class="space-y-6">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs">
        <div>
          <h1 class="text-xl font-bold text-slate-900">Manajemen Kategori Produk</h1>
          <p class="text-xs text-slate-500 mt-1">Kelola daftar kategori utama katalog PPOB Manza.</p>
        </div>
        <button
          @click="openCreateModal"
          type="button"
          class="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-xs transition flex items-center justify-center gap-2"
        >
          <Plus class="w-4 h-4" />
          <span>Tambah Kategori</span>
        </button>
      </div>

      <!-- Flash Status -->
      <div v-if="$page.props.flash.status" class="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold rounded-xl">
        {{ $page.props.flash.status }}
      </div>

      <!-- Table Section -->
      <div class="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
        <div class="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-4">
          <input
            v-model="search"
            type="text"
            placeholder="Cari kategori (nama, kode)..."
            class="px-3.5 py-2 bg-white border border-slate-300 text-slate-900 rounded-xl text-xs w-64 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            @input="debouncedSearch"
          />
          <span class="text-xs font-semibold text-slate-500">Total: {{ categories.length }} Kategori</span>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse text-xs">
            <thead>
              <tr class="border-b border-slate-200 bg-slate-100/70 text-slate-700 font-bold uppercase tracking-wider">
                <th class="py-3 px-4">Urutan</th>
                <th class="py-3 px-4">Kode</th>
                <th class="py-3 px-4">Nama Kategori</th>
                <th class="py-3 px-4">Deskripsi</th>
                <th class="py-3 px-4">Jumlah Produk</th>
                <th class="py-3 px-4">Status</th>
                <th class="py-3 px-4 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 text-slate-800">
              <tr v-for="cat in categories" :key="cat.id" class="hover:bg-slate-50/80 transition">
                <td class="py-3 px-4 font-mono font-bold text-slate-500">{{ cat.sort_order }}</td>
                <td class="py-3 px-4 font-mono font-bold text-indigo-600">{{ cat.code }}</td>
                <td class="py-3 px-4 font-bold text-slate-900">{{ cat.name }}</td>
                <td class="py-3 px-4 text-slate-500 max-w-xs truncate">{{ cat.description || '-' }}</td>
                <td class="py-3 px-4 font-semibold text-slate-700">{{ cat.products_count }} produk</td>
                <td class="py-3 px-4">
                  <span
                    :class="cat.is_active ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'"
                    class="px-2.5 py-1 rounded-full border text-[10px] font-bold"
                  >
                    {{ cat.is_active ? 'Aktif' : 'Nonaktif' }}
                  </span>
                </td>
                <td class="py-3 px-4 text-right space-x-2">
                  <button
                    @click="toggleActive(cat)"
                    type="button"
                    class="px-2.5 py-1 rounded-lg border text-[11px] font-semibold transition"
                    :class="cat.is_active ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100' : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'"
                  >
                    {{ cat.is_active ? 'Nonaktifkan' : 'Aktifkan' }}
                  </button>
                  <button @click="openEditModal(cat)" type="button" class="px-2.5 py-1 rounded-lg border border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200 text-[11px] font-semibold transition">
                    Edit
                  </button>
                  <button @click="deleteCategory(cat)" type="button" class="px-2.5 py-1 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 text-[11px] font-semibold transition">
                    Hapus
                  </button>
                </td>
              </tr>
              <tr v-if="categories.length === 0">
                <td colspan="7" class="py-8 text-center text-slate-500 text-xs">
                  Tidak ada data kategori produk.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Modal Create / Edit -->
      <div v-if="showModal" class="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 shadow-xl border border-slate-200">
          <h3 class="text-base font-bold text-slate-900">{{ isEditing ? 'Edit Kategori Produk' : 'Tambah Kategori Produk' }}</h3>

          <form @submit.prevent="submitForm" class="space-y-4">
            <div>
              <label class="block text-xs font-bold text-slate-700 mb-1">Kode Kategori</label>
              <input v-model="form.code" type="text" placeholder="misal: pulsa" required class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono" />
            </div>

            <div>
              <label class="block text-xs font-bold text-slate-700 mb-1">Nama Kategori</label>
              <input v-model="form.name" type="text" placeholder="misal: Pulsa Seluler" required class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>

            <div>
              <label class="block text-xs font-bold text-slate-700 mb-1">Deskripsi</label>
              <textarea v-model="form.description" rows="2" placeholder="Deskripsi singkat kategori..." class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"></textarea>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-bold text-slate-700 mb-1">Ikon Lucide</label>
                <input v-model="form.icon" type="text" placeholder="smartphone" class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label class="block text-xs font-bold text-slate-700 mb-1">Urutan (Sort Order)</label>
                <input v-model.number="form.sort_order" type="number" min="0" class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
            </div>

            <div class="flex items-center gap-2 pt-1">
              <input v-model="form.is_active" type="checkbox" id="cat_is_active" class="rounded text-indigo-600 focus:ring-indigo-500" />
              <label for="cat_is_active" class="text-xs font-semibold text-slate-700">Kategori Aktif</label>
            </div>

            <div class="flex justify-end gap-2 pt-2">
              <button type="button" @click="showModal = false" class="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition">Batal</button>
              <button type="submit" :disabled="form.processing" class="px-4 py-2 text-xs font-bold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition">
                Simpan
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </BackofficeLayout>
</template>

<script setup>
import { ref } from 'vue';
import { useForm, router } from '@inertiajs/vue3';
import BackofficeLayout from '@/Layouts/BackofficeLayout.vue';
import { Plus } from '@lucide/vue';

const props = defineProps({
  categories: Array,
  filters: Object,
});

const search = ref(props.filters?.search || '');
const showModal = ref(false);
const isEditing = ref(false);
const editingCategory = ref(null);

const form = useForm({
  code: '',
  name: '',
  description: '',
  icon: 'package',
  sort_order: 0,
  is_active: true,
});

const openCreateModal = () => {
  isEditing.value = false;
  editingCategory.value = null;
  form.reset();
  form.clearErrors();
  showModal.value = true;
};

const openEditModal = (cat) => {
  isEditing.value = true;
  editingCategory.value = cat;
  form.code = cat.code;
  form.name = cat.name;
  form.description = cat.description;
  form.icon = cat.icon;
  form.sort_order = cat.sort_order;
  form.is_active = cat.is_active;
  form.clearErrors();
  showModal.value = true;
};

const submitForm = () => {
  if (isEditing.value && editingCategory.value) {
    form.put(route('admin.catalogue.categories.update', editingCategory.value.id), {
      onSuccess: () => { showModal.value = false; },
    });
  } else {
    form.post(route('admin.catalogue.categories.store'), {
      onSuccess: () => { showModal.value = false; },
    });
  }
};

const toggleActive = (cat) => {
  router.patch(route('admin.catalogue.categories.toggle-active', cat.id), {
    is_active: !cat.is_active,
  });
};

const deleteCategory = (cat) => {
  if (confirm(`Apakah Anda yakin ingin menghapus kategori '${cat.name}'?`)) {
    router.delete(route('admin.catalogue.categories.destroy', cat.id));
  }
};

const debouncedSearch = () => {
  router.get(route('admin.catalogue.categories.index'), { search: search.value }, { preserveState: true, replace: true });
};
</script>
